import { api } from './client'
import { todayISO } from '@/lib/dates'
import type { Priority, PriorityHistory, PriorityStatus } from '@/types'

export function fetchPrioritiesByBucket(bucketId: string): Promise<Priority[]> {
  return api.get(`/api/priorities?bucketId=${bucketId}`)
}

export function fetchArchivedPriorities(): Promise<Priority[]> {
  return api.get('/api/priorities?scope=archived')
}

export function fetchAllActivePriorities(): Promise<Priority[]> {
  return api.get('/api/priorities?scope=active-all')
}

export function fetchPriority(id: string): Promise<Priority | null> {
  return api.get(`/api/priorities/${id}`)
}

export function fetchPriorityHistory(priorityId: string): Promise<PriorityHistory[]> {
  return api.get(`/api/priorities/${priorityId}/history`)
}

export async function createPriority(
  bucketId: string,
  title: string,
  extra?: Partial<
    Omit<Priority, 'id' | 'bucketId' | 'title' | 'rank' | 'status' | 'aiExcludable' | 'createdAt' | 'updatedAt'>
  >,
): Promise<string> {
  const { id } = await api.post<{ id: string }>('/api/priorities', { bucketId, title, ...extra })
  return id
}

export function updatePriority(id: string, data: Partial<Omit<Priority, 'id' | 'createdAt'>>): Promise<void> {
  return api.patch(`/api/priorities/${id}`, data)
}

export function setStatus(id: string, status: PriorityStatus, releaseNote?: string): Promise<void> {
  return api.post(`/api/priorities/${id}/status`, { status, releaseNote })
}

export function reorderPriority(
  id: string, bucketId: string, beforeRank: number | null, afterRank: number | null,
): Promise<void> {
  return api.post(`/api/priorities/${id}/reorder`, { bucketId, beforeRank, afterRank })
}

export function archivePriority(id: string): Promise<void> {
  return api.post(`/api/priorities/${id}/archive`)
}

export function markPriorityReviewed(id: string): Promise<void> {
  return api.post(`/api/priorities/${id}/mark-reviewed`, { today: todayISO() })
}

export function restorePriority(id: string): Promise<void> {
  return api.post(`/api/priorities/${id}/restore`)
}

export function permanentlyDeletePriority(id: string): Promise<void> {
  return api.delete(`/api/priorities/${id}`)
}

export async function duplicatePriority(id: string): Promise<string> {
  const { id: newId } = await api.post<{ id: string }>(`/api/priorities/${id}/duplicate`)
  return newId
}
